import React, {Component, ReactNode} from 'react';
import packageJson from '../package.json';
import {connect} from 'react-redux';
import {
  BrowserRouter,
  Switch,
  withRouter,
  Route,
  Redirect
} from 'react-router-dom';
import NavRoutes from './constants/route/NavRoutes';
import NotFound from './components/framework/notfound/NotFound';
import AuthUtils from './utils/AuthUtils';
import {_initApp, _userType, _groupId} from '@shared/store/actions/AuthActions';
import Loader from './components/framework/loader/Loader';
import {axios} from '@shared/axios';
import './style/style.scss';

import CustomRoute from './constants/route/CustomRoute';
import {BASE_ROUTE_ADMIN} from './constants/route/BaseRoute';
import Admin from './modules/admin/Admin';
import Logout from './modules/public/Logout/Logout';
import Storage from 'src/utils/Storage';
import {environment} from '@shared/environment/environment';
import {LinearProgress} from '@material-ui/core';
import {decrypt} from '@shared/utils/LocalStorage';
import PaymentRedirect from './modules/payments/PaymentRedirect/PaymentRedirect';
import {CommunicationService} from './service/CommunicationService';
import {PriorAuthorizationsDefaultFilter} from './modules/public/PriorAuthorizationsDefaultFilter/PriorAuthorizationDefaultFilter';

class App extends Component<any, any> {
  handleInvalidToken: any = null;
  constructor(props: any) {
    super(props);
    this.state = {
      loading: true,
      isNotAuthorized: false
    };
    this.handleAppEntry = this.handleAppEntry.bind(this);
    this.getAuthStatusFromStorage = this.getAuthStatusFromStorage.bind(this);
    this.plugInterceptors = this.plugInterceptors.bind(this);
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.handleInvalidToken);
  }
  componentDidMount() {
    const {history} = this.props;
    this.handleAppEntry();
    this.plugInterceptors();
    this.handleInvalidToken = (e: any) => {
      if (
        decrypt(e.key) === Storage.KEYS.PORTAL &&
        e.oldValue !== e.newValue &&
        !e.newValue
      ) {
        history.push(`/${NavRoutes.PUBLIC.LOGOUT}`);
        window.location.reload();
      }
    };
    window.addEventListener('storage', this.handleInvalidToken);
  }

  plugInterceptors(): void {
    const {history} = this.props;
    // Request interceptor
    axios.interceptors.request.use(
      function (config) {
        // Do something before request is sent
        return config;
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error);
      }
    );

    // Response interceptor
    axios.interceptors.response.use(
      function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger

        const apiVersion = new String(response.headers['x-version']);
        const currentVersion = Storage.getItem(Storage.KEYS.APP_VERSION);
        const portal = AuthUtils.resolvePortal();
        if (apiVersion) {
          // if server version newer
          Storage.setItem(Storage.KEYS.APP_VERSION, apiVersion.toString()); // set version update item so can refresh app later
          if (currentVersion !== apiVersion.toString()) {
            /**
             * Check if there is already a current version or the user is logged in
             */
            if (currentVersion || portal) {
              // Trigger an event for version update
              CommunicationService.sendEvent({
                action: 'APP_VERSION_UPDATED',
                data: null
              });
            }
            if (!portal) {
              window.location.reload();
            }
          }
        }

        return response;
      },
      error => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        // Logging out for unauthorised access
        if (error?.response?.status === 401) {
          history.push(`/${NavRoutes.PUBLIC.LOGOUT}`);
          window.location.reload();
          this.setState({isNotAuthorized: true});
        }
        return Promise.reject(error);
      }
    );
  }

  // Token can be verified here before preparing the return object
  getAuthStatusFromStorage() {
    return AuthUtils.resolvePortal();
  }

  async handleAppEntry() {
    const {initialRoute} = this.props;
    if (!initialRoute) {
      const portal = this.getAuthStatusFromStorage();
      if (portal) {
        this.props._initApp({initialRoute: portal});
        this.setState({loading: false});
      } else {
        setTimeout(() => {
          this.setState({loading: false});
        }, 1000);
      }
    } else {
      setTimeout(() => {
        this.setState({loading: false});
      }, 1000);
    }
  }

  render(): ReactNode {
    if (this.state.loading) {
      return (
        <div className="flex-centering">
          <Loader size={56} color="primary" />
        </div>
      );
    }
    const commonRoute = CustomRoute.commonRoute;
    const {initialRoute} = this.props;
    // setting up userType while refresh;
    if (Storage.getItem('userType')) {
      this.props._userType(Storage.getItem('userType'));
      this.props._groupId(Storage.getItem('org_group_id', true));
    }
    const returnRoute = CustomRoute.basicRoute(
      commonRoute,
      initialRoute || null,
      `/${initialRoute}`
    );
    if (this.state.isNotAuthorized) {
      return <Logout />;
    }
    return (
      <>
        <BrowserRouter>
          <React.Suspense fallback={<LinearProgress />}>
            <Switch>
              <Redirect
                to={`${
                  initialRoute ? '/' + initialRoute : commonRoute.redirectTo
                }`}
                from="/"
                exact
              />
              {returnRoute}
              <Route path={`/${NavRoutes.PUBLIC.LOGOUT}`} component={Logout} />
              <Route path={`/${BASE_ROUTE_ADMIN}`} component={Admin} />
              <Route
                path={`/:practiceShortCode`}
                exact
                component={PaymentRedirect}
              />
              <Route
                path={`/clinic-group/:clinic_group_id/clinic/:clinic_id/prior-authorizations`}
                exact
                component={PriorAuthorizationsDefaultFilter}
              />
              <Route path={`/${NavRoutes.PUBLIC[404]}`} component={NotFound} />
              <Redirect to={`/${NavRoutes.PUBLIC.LOGIN}`} />
            </Switch>
          </React.Suspense>
        </BrowserRouter>
        {environment.dev && (
          <div className={'build-version-number'}>
            <span>v{packageJson.version}</span>
          </div>
        )}
      </>
    );
  }
}

type StateObj = {
  auth: {initialRoute?: string};
};

function mapStateToProps(state: StateObj) {
  // Properties defined here (read from state) will be available as props in the component
  return {
    initialRoute: state.auth.initialRoute
  };
}

export default connect(mapStateToProps, {
  _initApp,
  _userType,
  _groupId
  // Binding this dispatcher to be available in props in the component
})(withRouter(App));
